/* iter-stripe-kill-tile — Prominent one-click payments ON/OFF tile.
 *
 * Sits above the three big group cards on the Admin Hub so a compromised
 * key or a runaway refund loop can be stopped in two clicks without
 * hunting through the Finance panel.
 *
 * Reads the same `integrations_enabled.stripe` flag the fine-grained
 * IntegrationsTogglePanel writes to and the public `/api/payments/config`
 * exposes, so all three surfaces stay coherent.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { CreditCard, ShieldOff, ShieldCheck, Loader2, AlertTriangle, History } from 'lucide-react';

function _fmtDuration(sinceIso) {
  if (!sinceIso) return null;
  const then = new Date(sinceIso).getTime();
  if (!then) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `${hrs} h ${mins % 60} min`;
}

function _fmtRelative(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins} min ago`;
  const hrs  = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  return `${days} d ago`;
}

export default function PaymentsKillSwitchTile() {
  const [cfg,   setCfg]   = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [reason,   setReason]   = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  // iter-stripe-audit — Last-5 flip history.
  const [audit,     setAudit]     = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.config();
      setCfg(r);
      setReason(r?.payments_downtime_reason || '');
    } catch { setCfg(null); }
    try {
      setAudit((await adminApi.paymentsToggleAudit(5)).rows || []);
    } catch { setAudit([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;
  const stripeOn = cfg?.integrations_enabled?.stripe !== false;
  const offFor   = _fmtDuration(cfg?.payments_disabled_since);

  const flip = async (next) => {
    setBusy(true);
    try {
      await adminApi.saveConfig({
        integrations_enabled: { stripe: next },
        // Only save the reason when flipping OFF so the previous
        // downtime message doesn't linger after recovery.
        payments_downtime_reason: next ? '' : (reason || 'Payments paused by ops').slice(0, 220),
      });
      toast.success(next
        ? 'Stripe payments RE-ENABLED — live charges resume immediately.'
        : 'Stripe payments PAUSED — checkout blocked, downtime banner live.');
      setShowConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const tone = stripeOn
    ? 'border-emerald-400/60 bg-emerald-50/60 text-emerald-900'
    : 'border-red-400/70 bg-red-50/70 text-red-900';

  return (
    <div
      data-testid="admin-hub-payments-kill-tile"
      data-state={stripeOn ? 'on' : 'off'}
      className={`mt-4 rounded-md border-2 p-4 shadow-sm ${tone}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-white/70">
          {stripeOn
            ? <ShieldCheck className="h-4 w-4 text-emerald-700" />
            : <ShieldOff  className="h-4 w-4 text-red-700" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold">
            <CreditCard className="h-3.5 w-3.5 opacity-70" />
            Stripe payments — {stripeOn ? 'LIVE' : 'PAUSED'}
            {!stripeOn && offFor && (
              <span className="rounded-full bg-red-200/80 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider">
                off for {offFor}
              </span>
            )}
          </div>
          <p className="text-[11px] leading-snug opacity-80">
            {stripeOn
              ? 'Card charges, subscriptions, Book Store checkouts, and Weekly Maven are all live. Auto-recover cron re-enables payments after 60 min if left OFF.'
              : `Every checkout endpoint returns 503 and the site-wide downtime banner is showing. ${cfg?.payments_downtime_reason ? `Current reason: "${cfg.payments_downtime_reason.slice(0, 120)}"` : ''}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {stripeOn ? (
            !showConfirm ? (
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                data-testid="admin-hub-payments-pause-btn"
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-sm border border-red-400/60 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-60"
              >
                <ShieldOff className="h-3.5 w-3.5" /> Pause payments
              </button>
            ) : (
              <div className="flex items-center gap-2 rounded-sm border border-red-400/60 bg-white p-2">
                <AlertTriangle className="h-3.5 w-3.5 text-red-700" />
                <span className="text-[10.5px] font-semibold uppercase text-red-800">Confirm?</span>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Downtime reason (optional)"
                  data-testid="admin-hub-payments-pause-reason"
                  maxLength={220}
                  className="h-7 w-56 rounded-sm border border-red-300/60 bg-white px-2 text-[11px] outline-none focus:border-red-500"
                />
                <button
                  type="button"
                  onClick={() => flip(false)}
                  disabled={busy}
                  data-testid="admin-hub-payments-pause-confirm"
                  className="inline-flex items-center gap-1 rounded-sm bg-red-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-red-800 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
                  Yes, pause
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  disabled={busy}
                  className="rounded-sm px-2 py-1 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            )
          ) : (
            <button
              type="button"
              onClick={() => flip(true)}
              disabled={busy}
              data-testid="admin-hub-payments-resume-btn"
              className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-700 px-3 py-1.5 text-[11.5px] font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Resume payments
            </button>
          )}
        </div>
      </div>

      {/* iter-stripe-audit — Last 5 flips. Collapsed by default so the tile
          stays quiet on days when nothing dramatic happened. */}
      {audit && audit.length > 0 && (
        <div className="mt-2 border-t border-current/20 pt-2 text-[10.5px]"
             data-testid="admin-hub-payments-audit">
          <button
            type="button"
            onClick={() => setAuditOpen((v) => !v)}
            className="inline-flex items-center gap-1 opacity-70 hover:opacity-100"
            data-testid="admin-hub-payments-audit-toggle"
          >
            <History className="h-3 w-3" />
            {auditOpen ? 'Hide' : 'Show'} last {audit.length} flip{audit.length === 1 ? '' : 's'}
          </button>
          {auditOpen && (
            <ol className="mt-1 space-y-1" data-testid="admin-hub-payments-audit-list">
              {audit.map((r) => (
                <li key={r.id}
                    data-testid={`admin-hub-payments-audit-row-${r.id}`}
                    className="flex items-start gap-2 rounded-sm bg-white/60 px-2 py-1">
                  <span className={`mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                    r.to_state === 'on' ? 'bg-emerald-600' : 'bg-red-600'
                  }`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10.5px]">
                      <strong>{r.actor}</strong>
                      {' '}{r.from_state} → <strong>{r.to_state}</strong>
                      {' · '}
                      <span className="opacity-70">{_fmtRelative(r.at)}</span>
                      {r.trigger === 'autorecover' && (
                        <span className="ml-1 rounded-full bg-current/10 px-1 text-[9.5px] uppercase tracking-wider opacity-70">auto</span>
                      )}
                    </div>
                    {r.reason && (
                      <div className="truncate text-[10px] opacity-70" title={r.reason}>
                        “{r.reason}”
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
